import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { ClassCodeComboBoxModel } from './class-code-combo-box.model';
import { GetClassListService } from '../class-code-combo-box/api-services/get-class-list.service';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 共通部品 部門コードコンボボックスを制御するクラス
 *
 * @export ClassCodeComboBoxComponent
 * @class ClassCodeComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-class-code-combo-box',
  templateUrl: 'class-code-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ClassCodeComboBoxComponent,
      multi: true
    },
    {
      // データをとるサービスを複数newしたいため、こちらに記述
      provide: GetClassListService,
      // 初期化処理を指定
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetClassListService(plessHttp, dialogService);
      },
      // 初期化処理で DI するクラスを指定
      // 項番2473 START 連合会問合せNo.45対応
      // deps: [PlessHttp, PlessDialogService]
      deps: [PlessHttp, PlessDialogServiceSub]
      // 項番2473 END 連合会問合せNo.45対応
    },
  ],
})

export class ClassCodeComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  // 受注コード
  public selectedOrderCode: string = <string>'';
  // 部門コード
  public selectedClassCode: string = <string>'';
  // 部門名
  public selectedClassCodeName: string = <string>'';
  // 部門分類管理NO
  public selectedClassDivNum: string = <string>'';
  // 部門コードモデルインスタンス
  public model: ClassCodeComboBoxModel = new ClassCodeComboBoxModel();
  // 選択index
  public selectedIndex: number = <number>-1;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;
  @Input() required: boolean = <boolean>false;
  @Input() orderCode: string = <string>'';
  @Input() disabled: boolean = <boolean>false;
  @Input() isInitOrderCode: string = <string>'';
  @Input() initAutoLoad: boolean = <boolean>true;
  @Input() left: number = <number>10;
  @Input() top: number = <number>10;
  @Input() width: number | string; // どちらでも受けてTemplateに判断させる
  @Input() height: number = <number>20;
  @Input() public initFocusItem: any = null;                                    // ロード完了後フォーカスを移動させるid
  @Input() public tabindex: number = null;
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ
  @Input() public autofocus: boolean = <boolean>false;
  @Input() forcedEnabledMode: string = null;

  /** コンボボックスの参照 */
  @ViewChild('comboBox', { static: true }) comboBox: ElementRef;

  /**
   * Creates an instance of ClassCodeComboBoxComponent.
   * @param {ElementRef} elementRef
   * @param {GetClassListService} getClassListService
   * @memberof ClassCodeComboBoxComponent
   */
  constructor(
    public elementRef: ElementRef,
    private getClassListService: GetClassListService,
  ) { }

  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedClassCode) {
      this.selectedClassCode = text;
      this.setClassCode(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof ClassCodeComboBoxComponent
   */
  ngOnInit(): void {
    // コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof ClassCodeComboBoxComponent
   */
  ngAfterViewInit(): void {
    // データ取得
    this.getClassListService.execute(this.model, this.orderCode);
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof ClassCodeComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
      }
    });
  }

  /**
   * コンポーネント終了時
   *
   * @memberof ClassCodeComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.clear();
  }

  /**
   * コンボボックスの選択部門コードを切り替える
   *
   * @param {string} classCode 部門コード
   * @memberof ClassCodeComboBoxComponent
   */
  public setClassCode(classCode: string): void {
    this.model.setClassCode = classCode;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedClassCode();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof ClassCodeComboBoxComponent
   */
  public updateSelectedClassCode(): void {
    const classList: any[] = this.model.classList;
    for (let i = Number(0); i < classList.length; i++) {
      if (PlessViewUtilsSub.trim(classList[i].ClassCode) === this.model.setClassCode) {
        this.selectedIndex = i;
        this.update(null);
      }
    }

    // バインドフィールドを更新
    if (this.initFocusItem != null) {
      let queryedElement;
      if (this.initFocusItem instanceof HTMLElement) {
        queryedElement = this.initFocusItem.querySelector('textarea, input, select');
      } else {
        queryedElement = this.initFocusItem.elementRef.nativeElement.querySelector('textarea, input, select');
      }
      (queryedElement ? <HTMLElement>queryedElement : this.initFocusItem).focus();
    }
  }

  /**
   * 再ロード
   *
   * @param {*} e
   * @returns {void}
   * @memberof ClassCodeComboBoxComponent
   */
  public update(e: any): void {

    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }

    if (this.selectedIndex === -1) {
      this.selectedClassCode = <string>'';
      this.selectedClassCodeName = <string>'';
      this.selectedClassDivNum = <string>'';
      return;
    }

    this.selectedClassCode = this.model.classList[this.selectedIndex].ClassCode;
    this.selectedClassCodeName = this.model.classList[this.selectedIndex].ClassName;
    this.selectedClassDivNum = this.model.classList[this.selectedIndex].ClassDivNum;
  }

  /**
   * 初期化処理
   *
   * @memberof ClassCodeComboBoxComponent
   */
  public clear(): void {
    this.selectedOrderCode = <string>'';
    this.selectedClassCode = <string>'';
    this.selectedClassCodeName = <string>'';
    this.selectedClassDivNum = <string>'';
    this.selectedIndex = <number>-1;
    this.model = new ClassCodeComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @param {number} index
   * @memberof ClassCodeComboBoxComponent
   */
  public setSelectedIndex(index: number): void {
    this.model.selectedIndex = index;
    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedIndex();
    }
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @memberof ClassCodeComboBoxComponent
   */
  public updateSelectedIndex(): void {
    if (this.model.selectedIndex >= 0) {
      this.selectedIndex = this.model.selectedIndex;
      this.model.selectedIndex = -1;
      this.update(null);
    }
  }

  /**
   * setFocusOverride※テキスト選択解除の為
   *
   * @memberof ClassCodeComboBoxComponent
   */
  public setFocus(): void {
    this.comboBox.nativeElement.focus();
  }

}
